// Seed data for jurisdiction-aware minimum wage and local income tax
// (Phase 17). Backward compatible: if config doesn't carry these blocks,
// engine falls back to the legacy flat federal min wage and skips local tax.
//
// 2026 rates were taken from the user's provided spec. Where the spec said
// "more can be added" we extended the local-income-tax set per their choice
// to include OH cities, MD/IN counties, KY occupational, and a representative
// PA municipal LST baseline.

export const SEED_MIN_WAGE_JURISDICTIONS = {
  federal: { hourly: 7.25, effectiveDate: '2009-07-24', lastUpdated: null },
  states: {
    AK: { hourly: 11.91, effectiveDate: '2026-01-01' },
    AZ: { hourly: 14.70, effectiveDate: '2026-01-01' },
    CA: { hourly: 16.50, effectiveDate: '2026-01-01' },
    CO: { hourly: 14.81, effectiveDate: '2026-01-01' },
    CT: { hourly: 16.35, effectiveDate: '2026-01-01' },
    DC: { hourly: 17.50, effectiveDate: '2026-07-01' },
    FL: { hourly: 14.00, effectiveDate: '2026-09-30' },
    HI: { hourly: 14.00, effectiveDate: '2024-01-01' },
    IL: { hourly: 15.00, effectiveDate: '2026-01-01' },
    MA: { hourly: 15.00, effectiveDate: '2024-01-01' },
    MD: { hourly: 15.00, effectiveDate: '2024-01-01' },
    ME: { hourly: 14.65, effectiveDate: '2026-01-01' },
    MI: { hourly: 12.48, effectiveDate: '2026-02-21' },
    MN: { hourly: 11.13, effectiveDate: '2026-01-01' },
    MO: { hourly: 13.75, effectiveDate: '2026-01-01' },
    MT: { hourly: 10.55, effectiveDate: '2026-01-01' },
    NE: { hourly: 13.50, effectiveDate: '2026-01-01' },
    NJ: { hourly: 15.49, effectiveDate: '2026-01-01' },
    NM: { hourly: 12.00, effectiveDate: '2023-01-01' },
    NV: { hourly: 12.00, effectiveDate: '2024-07-01' },
    NY: { hourly: 15.50, effectiveDate: '2026-01-01' },
    OH: { hourly: 10.70, effectiveDate: '2026-01-01' },
    OR: { hourly: 14.70, effectiveDate: '2024-07-01' },
    RI: { hourly: 15.00, effectiveDate: '2025-01-01' },
    SD: { hourly: 11.50, effectiveDate: '2026-01-01' },
    VT: { hourly: 14.01, effectiveDate: '2026-01-01' },
    WA: { hourly: 16.66, effectiveDate: '2026-01-01' },
    WV: { hourly: 8.75, effectiveDate: '2016-01-01' },
  },
  cities: {
    'Seattle, WA': { hourly: 20.76, effectiveDate: '2026-01-01', state: 'WA' },
    'San Francisco, CA': { hourly: 18.67, effectiveDate: '2024-07-01', state: 'CA' },
    'New York City, NY': { hourly: 16.50, effectiveDate: '2026-01-01', state: 'NY' },
    'Long Island, NY': { hourly: 16.50, effectiveDate: '2026-01-01', state: 'NY' },
    'Westchester County, NY': { hourly: 16.50, effectiveDate: '2026-01-01', state: 'NY' },
    'Denver, CO': { hourly: 18.81, effectiveDate: '2026-01-01', state: 'CO' },
    'Chicago, IL': { hourly: 16.20, effectiveDate: '2024-07-01', state: 'IL' },
  },
};

// ---- Local Income Tax ----
// `flatRate` is applied to annual premium when conformsToFederal is true.
// `nonResidentRate` is configured for completeness (Phase 17 assumes resident).
// `brackets[filing]` is supported for progressive jurisdictions (NYC).
export const SEED_LOCAL_INCOME_TAXES = {
  // ---- New York ----
  'New York City, NY': {
    type: 'city_resident',
    conformsToFederal: true,
    brackets: {
      single: [
        { from: 0, to: 12000, rate: 0.03078 },
        { from: 12000, to: 25000, rate: 0.03762 },
        { from: 25000, to: 50000, rate: 0.03819 },
        { from: 50000, to: Infinity, rate: 0.03876 },
      ],
      married: [
        { from: 0, to: 21600, rate: 0.03078 },
        { from: 21600, to: 45000, rate: 0.03762 },
        { from: 45000, to: 90000, rate: 0.03819 },
        { from: 90000, to: Infinity, rate: 0.03876 },
      ],
      hoh: [
        { from: 0, to: 14400, rate: 0.03078 },
        { from: 14400, to: 30000, rate: 0.03762 },
        { from: 30000, to: 60000, rate: 0.03819 },
        { from: 60000, to: Infinity, rate: 0.03876 },
      ],
    },
    state: 'NY',
    effectiveDate: '2026-01-01',
  },
  'Yonkers, NY': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.0161,
    state: 'NY',
    effectiveDate: '2026-01-01',
    notes: 'Approximation: Yonkers resident tax is 16.75% of NY state liability; modelled as flat 1.61% of premium.',
  },

  // ---- Pennsylvania ----
  'Philadelphia, PA': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.0375,
    nonResidentRate: 0.0344,
    state: 'PA',
    effectiveDate: '2026-07-01',
  },
  'Pittsburgh, PA': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.03,
    nonResidentRate: 0.01,
    state: 'PA',
    effectiveDate: '2024-01-01',
    notes: 'Pittsburgh resident EIT (1%) + School District (2%) — combined 3% baseline.',
  },
  'Scranton, PA': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.034,
    state: 'PA',
    effectiveDate: '2024-01-01',
    notes: 'Scranton municipal + school district EIT.',
  },
  'PA Default LST, PA': {
    type: 'baseline',
    conformsToFederal: true,
    flatRate: 0.01,
    state: 'PA',
    effectiveDate: '2024-01-01',
    notes: 'Pennsylvania municipal Local Earned Income Tax — common 1% baseline. Override per municipality as needed.',
  },

  // ---- Michigan ----
  'Detroit, MI': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.024,
    nonResidentRate: 0.012,
    state: 'MI',
    effectiveDate: '2024-01-01',
  },
  'Grand Rapids, MI': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.015,
    nonResidentRate: 0.0075,
    state: 'MI',
    effectiveDate: '2024-01-01',
  },

  // ---- Delaware ----
  'Wilmington, DE': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.0125,
    state: 'DE',
    effectiveDate: '2024-01-01',
  },

  // ---- Missouri ----
  'St. Louis, MO': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.01,
    state: 'MO',
    effectiveDate: '2024-01-01',
  },
  'Kansas City, MO': {
    type: 'city_resident',
    conformsToFederal: true,
    flatRate: 0.01,
    state: 'MO',
    effectiveDate: '2024-01-01',
  },

  // ---- Ohio (representative city sample) ----
  'Columbus, OH': { type: 'city_resident', conformsToFederal: true, flatRate: 0.025, state: 'OH', effectiveDate: '2024-01-01' },
  'Cleveland, OH': { type: 'city_resident', conformsToFederal: true, flatRate: 0.025, state: 'OH', effectiveDate: '2024-01-01' },
  'Cincinnati, OH': { type: 'city_resident', conformsToFederal: true, flatRate: 0.018, state: 'OH', effectiveDate: '2024-01-01' },
  'Toledo, OH': { type: 'city_resident', conformsToFederal: true, flatRate: 0.025, state: 'OH', effectiveDate: '2024-01-01' },
  'Akron, OH': { type: 'city_resident', conformsToFederal: true, flatRate: 0.025, state: 'OH', effectiveDate: '2024-01-01' },

  // ---- Maryland (counties — applied to MD residents) ----
  'Baltimore City, MD': { type: 'county_resident', conformsToFederal: true, flatRate: 0.032, state: 'MD', effectiveDate: '2024-01-01' },
  'Montgomery County, MD': { type: 'county_resident', conformsToFederal: true, flatRate: 0.032, state: 'MD', effectiveDate: '2024-01-01' },
  'Prince George\'s County, MD': { type: 'county_resident', conformsToFederal: true, flatRate: 0.032, state: 'MD', effectiveDate: '2024-01-01' },
  'Howard County, MD': { type: 'county_resident', conformsToFederal: true, flatRate: 0.032, state: 'MD', effectiveDate: '2024-01-01' },

  // ---- Indiana (counties — most counties have a local rate) ----
  'Marion County, IN': { type: 'county_resident', conformsToFederal: true, flatRate: 0.0202, state: 'IN', effectiveDate: '2024-01-01' },
  'Lake County, IN': { type: 'county_resident', conformsToFederal: true, flatRate: 0.015, state: 'IN', effectiveDate: '2024-01-01' },
  'Allen County, IN': { type: 'county_resident', conformsToFederal: true, flatRate: 0.0148, state: 'IN', effectiveDate: '2024-01-01' },

  // ---- Kentucky (occupational license tax — major jurisdictions) ----
  'Louisville Metro, KY': { type: 'occupational', conformsToFederal: true, flatRate: 0.022, state: 'KY', effectiveDate: '2024-01-01', notes: 'Louisville Metro occupational license tax.' },
  'Lexington, KY': { type: 'occupational', conformsToFederal: true, flatRate: 0.0225, state: 'KY', effectiveDate: '2024-01-01' },
  'Bowling Green, KY': { type: 'occupational', conformsToFederal: true, flatRate: 0.0185, state: 'KY', effectiveDate: '2024-01-01' },
};

// ---- ZIP → Jurisdiction ----
// Curated dataset: covers ZIPs that map into any configured local-tax
// jurisdiction or a city in `cities` minimum-wage list. ZIPs outside this
// set won't trigger a local-tax / city-min-wage application anyway, so a
// missing entry is functionally equivalent to "no local jurisdiction".
//
// This avoids embedding a 5MB full US ZIP database while still resolving
// the jurisdictions that meaningfully affect Section 125 outcomes.
export const SEED_ZIP_TO_JURISDICTION = {
  // NYC (5 boroughs)
  '10001': { city: 'New York City', state: 'NY' }, '10002': { city: 'New York City', state: 'NY' },
  '10003': { city: 'New York City', state: 'NY' }, '10004': { city: 'New York City', state: 'NY' },
  '10005': { city: 'New York City', state: 'NY' }, '10006': { city: 'New York City', state: 'NY' },
  '10007': { city: 'New York City', state: 'NY' }, '10009': { city: 'New York City', state: 'NY' },
  '10010': { city: 'New York City', state: 'NY' }, '10011': { city: 'New York City', state: 'NY' },
  '10012': { city: 'New York City', state: 'NY' }, '10013': { city: 'New York City', state: 'NY' },
  '10014': { city: 'New York City', state: 'NY' }, '10016': { city: 'New York City', state: 'NY' },
  '10017': { city: 'New York City', state: 'NY' }, '10018': { city: 'New York City', state: 'NY' },
  '10019': { city: 'New York City', state: 'NY' }, '10020': { city: 'New York City', state: 'NY' },
  '10021': { city: 'New York City', state: 'NY' }, '10022': { city: 'New York City', state: 'NY' },
  '10023': { city: 'New York City', state: 'NY' }, '10024': { city: 'New York City', state: 'NY' },
  '10025': { city: 'New York City', state: 'NY' }, '10026': { city: 'New York City', state: 'NY' },
  '10027': { city: 'New York City', state: 'NY' }, '10028': { city: 'New York City', state: 'NY' },
  '10029': { city: 'New York City', state: 'NY' }, '10030': { city: 'New York City', state: 'NY' },
  '10031': { city: 'New York City', state: 'NY' }, '10032': { city: 'New York City', state: 'NY' },
  '10033': { city: 'New York City', state: 'NY' }, '10034': { city: 'New York City', state: 'NY' },
  '10035': { city: 'New York City', state: 'NY' }, '10036': { city: 'New York City', state: 'NY' },
  '10037': { city: 'New York City', state: 'NY' }, '10038': { city: 'New York City', state: 'NY' },
  '10039': { city: 'New York City', state: 'NY' }, '10040': { city: 'New York City', state: 'NY' },
  '10044': { city: 'New York City', state: 'NY' }, '10128': { city: 'New York City', state: 'NY' },
  '10280': { city: 'New York City', state: 'NY' }, '11201': { city: 'New York City', state: 'NY' },
  '11203': { city: 'New York City', state: 'NY' }, '11205': { city: 'New York City', state: 'NY' },
  '11206': { city: 'New York City', state: 'NY' }, '11215': { city: 'New York City', state: 'NY' },
  '11217': { city: 'New York City', state: 'NY' }, '11220': { city: 'New York City', state: 'NY' },
  '11221': { city: 'New York City', state: 'NY' }, '11222': { city: 'New York City', state: 'NY' },
  '11225': { city: 'New York City', state: 'NY' }, '11226': { city: 'New York City', state: 'NY' },
  '11231': { city: 'New York City', state: 'NY' }, '11232': { city: 'New York City', state: 'NY' },
  '11237': { city: 'New York City', state: 'NY' }, '11238': { city: 'New York City', state: 'NY' },
  '11354': { city: 'New York City', state: 'NY' }, '11355': { city: 'New York City', state: 'NY' },
  '11368': { city: 'New York City', state: 'NY' }, '11375': { city: 'New York City', state: 'NY' },
  '11377': { city: 'New York City', state: 'NY' }, '11385': { city: 'New York City', state: 'NY' },
  '10301': { city: 'New York City', state: 'NY' }, '10303': { city: 'New York City', state: 'NY' },
  '10314': { city: 'New York City', state: 'NY' }, '10451': { city: 'New York City', state: 'NY' },
  '10453': { city: 'New York City', state: 'NY' }, '10455': { city: 'New York City', state: 'NY' },
  '10456': { city: 'New York City', state: 'NY' }, '10458': { city: 'New York City', state: 'NY' },
  '10460': { city: 'New York City', state: 'NY' }, '10462': { city: 'New York City', state: 'NY' },
  '10463': { city: 'New York City', state: 'NY' }, '10467': { city: 'New York City', state: 'NY' },
  '10468': { city: 'New York City', state: 'NY' }, '10469': { city: 'New York City', state: 'NY' },
  '10470': { city: 'New York City', state: 'NY' }, '10471': { city: 'New York City', state: 'NY' },
  '10472': { city: 'New York City', state: 'NY' }, '10473': { city: 'New York City', state: 'NY' },
  '10475': { city: 'New York City', state: 'NY' },

  // Yonkers
  '10701': { city: 'Yonkers', state: 'NY' }, '10703': { city: 'Yonkers', state: 'NY' },
  '10704': { city: 'Yonkers', state: 'NY' }, '10705': { city: 'Yonkers', state: 'NY' },

  // Long Island min-wage zone (Nassau/Suffolk samples)
  '11550': { city: 'Long Island', state: 'NY' }, '11756': { city: 'Long Island', state: 'NY' },
  '11801': { city: 'Long Island', state: 'NY' }, '11901': { city: 'Long Island', state: 'NY' },

  // Westchester County samples
  '10601': { city: 'Westchester County', state: 'NY' },
  '10510': { city: 'Westchester County', state: 'NY' },
  '10580': { city: 'Westchester County', state: 'NY' },

  // Philadelphia
  '19102': { city: 'Philadelphia', state: 'PA' }, '19103': { city: 'Philadelphia', state: 'PA' },
  '19104': { city: 'Philadelphia', state: 'PA' }, '19106': { city: 'Philadelphia', state: 'PA' },
  '19107': { city: 'Philadelphia', state: 'PA' }, '19111': { city: 'Philadelphia', state: 'PA' },
  '19121': { city: 'Philadelphia', state: 'PA' }, '19124': { city: 'Philadelphia', state: 'PA' },
  '19130': { city: 'Philadelphia', state: 'PA' }, '19134': { city: 'Philadelphia', state: 'PA' },
  '19139': { city: 'Philadelphia', state: 'PA' }, '19143': { city: 'Philadelphia', state: 'PA' },
  '19145': { city: 'Philadelphia', state: 'PA' }, '19146': { city: 'Philadelphia', state: 'PA' },
  '19147': { city: 'Philadelphia', state: 'PA' }, '19148': { city: 'Philadelphia', state: 'PA' },

  // Pittsburgh
  '15201': { city: 'Pittsburgh', state: 'PA' }, '15203': { city: 'Pittsburgh', state: 'PA' },
  '15206': { city: 'Pittsburgh', state: 'PA' }, '15213': { city: 'Pittsburgh', state: 'PA' },
  '15217': { city: 'Pittsburgh', state: 'PA' }, '15219': { city: 'Pittsburgh', state: 'PA' },
  '15222': { city: 'Pittsburgh', state: 'PA' }, '15232': { city: 'Pittsburgh', state: 'PA' },

  // Detroit
  '48201': { city: 'Detroit', state: 'MI' }, '48202': { city: 'Detroit', state: 'MI' },
  '48207': { city: 'Detroit', state: 'MI' }, '48208': { city: 'Detroit', state: 'MI' },
  '48214': { city: 'Detroit', state: 'MI' }, '48226': { city: 'Detroit', state: 'MI' },

  // Grand Rapids
  '49503': { city: 'Grand Rapids', state: 'MI' }, '49506': { city: 'Grand Rapids', state: 'MI' },
  '49507': { city: 'Grand Rapids', state: 'MI' }, '49546': { city: 'Grand Rapids', state: 'MI' },

  // Wilmington DE
  '19801': { city: 'Wilmington', state: 'DE' }, '19802': { city: 'Wilmington', state: 'DE' },
  '19805': { city: 'Wilmington', state: 'DE' }, '19806': { city: 'Wilmington', state: 'DE' },

  // St Louis MO + KCMO
  '63101': { city: 'St. Louis', state: 'MO' }, '63103': { city: 'St. Louis', state: 'MO' },
  '63104': { city: 'St. Louis', state: 'MO' }, '63110': { city: 'St. Louis', state: 'MO' },
  '64108': { city: 'Kansas City', state: 'MO' }, '64111': { city: 'Kansas City', state: 'MO' },
  '64113': { city: 'Kansas City', state: 'MO' }, '64127': { city: 'Kansas City', state: 'MO' },

  // Ohio cities
  '43215': { city: 'Columbus', state: 'OH' }, '43219': { city: 'Columbus', state: 'OH' },
  '44113': { city: 'Cleveland', state: 'OH' }, '44114': { city: 'Cleveland', state: 'OH' },
  '45202': { city: 'Cincinnati', state: 'OH' }, '45219': { city: 'Cincinnati', state: 'OH' },
  '43604': { city: 'Toledo', state: 'OH' },
  '44308': { city: 'Akron', state: 'OH' },

  // Maryland
  '21201': { city: 'Baltimore City', state: 'MD' }, '21218': { city: 'Baltimore City', state: 'MD' },
  '20850': { city: 'Montgomery County', state: 'MD' }, '20910': { city: 'Montgomery County', state: 'MD' },
  '20705': { city: "Prince George's County", state: 'MD' },
  '21042': { city: 'Howard County', state: 'MD' },

  // Indiana
  '46202': { city: 'Marion County', state: 'IN' }, '46204': { city: 'Marion County', state: 'IN' },
  '46307': { city: 'Lake County', state: 'IN' },
  '46802': { city: 'Allen County', state: 'IN' },

  // Kentucky
  '40202': { city: 'Louisville Metro', state: 'KY' }, '40204': { city: 'Louisville Metro', state: 'KY' },
  '40503': { city: 'Lexington', state: 'KY' }, '40506': { city: 'Lexington', state: 'KY' },
  '42101': { city: 'Bowling Green', state: 'KY' },

  // Min-wage cities (Seattle, SF, Denver, Chicago)
  '98101': { city: 'Seattle', state: 'WA' }, '98109': { city: 'Seattle', state: 'WA' },
  '98112': { city: 'Seattle', state: 'WA' }, '98119': { city: 'Seattle', state: 'WA' },
  '94103': { city: 'San Francisco', state: 'CA' }, '94110': { city: 'San Francisco', state: 'CA' },
  '94117': { city: 'San Francisco', state: 'CA' }, '94133': { city: 'San Francisco', state: 'CA' },
  '80202': { city: 'Denver', state: 'CO' }, '80205': { city: 'Denver', state: 'CO' },
  '80218': { city: 'Denver', state: 'CO' },
  '60601': { city: 'Chicago', state: 'IL' }, '60611': { city: 'Chicago', state: 'IL' },
  '60614': { city: 'Chicago', state: 'IL' }, '60622': { city: 'Chicago', state: 'IL' },
};
